import React, { useState, useEffect, useRef, useCallback } from "react";

const GooglePlacesAutocomplete = ({ placeholder, result, key, value = "", required = false, disabled = false }) => {
  const [searchText, setSearchText] = useState(value);
  const [suggestions, setSuggestions] = useState([]);
  const debounceTimeout = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    setSearchText(value);
  }, [value]);

  const fetchSuggestions = useCallback(async (input) => {
    if (!input || input.trim().length < 3) {
      setSuggestions([]);
      return;
    }
    try {
      if (!window.google?.maps?.places?.AutocompleteSuggestion) {
        // Fallback: try importing the library
        if (window.google?.maps?.importLibrary) {
          await window.google.maps.importLibrary("places");
        }
      }
      const { suggestions: results } = await window.google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
        input,
        includedPrimaryTypes: ["geocode"],
      });
      setSuggestions(results || []);
    } catch (e) {
      console.warn("AutocompleteSuggestion fetch failed:", e.message);
      setSuggestions([]);
    }
  }, []);

  const handleInputChange = (e) => {
    const input = e.target.value;
    setSearchText(input);
    clearTimeout(debounceTimeout.current);
    debounceTimeout.current = setTimeout(() => {
      fetchSuggestions(input);
    }, 400);
  };

  const handleSelect = async (suggestion) => {
    try {
      const place = suggestion.placePrediction.toPlace();
      await place.fetchFields({ fields: ["formattedAddress", "addressComponents", "location"] });

      // Convert to legacy format for compatibility with callers
      const legacyPlace = {
        formatted_address: place.formattedAddress,
        geometry: place.location ? {
          location: {
            lat: () => place.location.lat(),
            lng: () => place.location.lng(),
          }
        } : undefined,
        address_components: (place.addressComponents || []).map(c => ({
          long_name: c.longText || "",
          short_name: c.shortText || "",
          types: c.types || [],
        })),
        place_id: suggestion.placePrediction.placeId,
      };

      setSearchText(place.formattedAddress || "");
      setSuggestions([]);
      result({ event: "placeChange", value: place.formattedAddress || "", place: legacyPlace });
    } catch (e) {
      console.warn("Place fetch failed:", e.message);
    }
  };

  const handleBlur = () => {
    setTimeout(() => setSuggestions([]), 300);
  };

  return (
    <div ref={containerRef} style={{ position: "relative" }}>
      <input
        key={key}
        type="text"
        placeholder={placeholder || "Enter a location"}
        value={searchText}
        required={required}
        disabled={disabled}
        className="form-control py-4"
        onChange={handleInputChange}
        onBlur={handleBlur}
      />
      {suggestions.length > 0 && (
        <ul style={{
          position: "absolute",
          top: "100%",
          left: 0,
          right: 0,
          zIndex: 9999,
          background: "white",
          border: "1px solid #ddd",
          listStyle: "none",
          margin: 0,
          padding: 0,
          maxHeight: "200px",
          overflowY: "auto",
          boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
          borderRadius: "0 0 4px 4px",
        }}>
          {suggestions.map((s, i) => (
            <li
              key={s.placePrediction?.placeId || i}
              style={{ padding: "10px 12px", cursor: "pointer", borderBottom: "1px solid #f0f0f0" }}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => handleSelect(s)}
            >
              <div style={{ fontWeight: 500 }}>{s.placePrediction?.mainText?.text || ""}</div>
              <div style={{ fontSize: "12px", color: "#888" }}>{s.placePrediction?.secondaryText?.text || ""}</div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default GooglePlacesAutocomplete;
